import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { projectsApi } from "@/api/projects";
import { ApiError } from "@/api/client";
import { isAuthenticated } from "@/auth/store";

export const Route = createFileRoute("/invite/$token")({
  component: InviteAcceptPage,
});

// Flow:
//   1. User clicks the email link → here.
//   2. If not signed in: stash the token in sessionStorage and bounce to /login.
//      After magic-link sign-in completes, /auth/callback sees the stash and
//      resumes by redirecting back here.
//   3. If signed in: POST /invites/accept and redirect to the project.
//
// The accept token is a 32-byte random base64url string — fine to put
// in sessionStorage; it's single-use server-side and TTL'd to 7 days.

const STASH_KEY = "tt-pending-invite-token";

function InviteAcceptPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"working" | "error">("working");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    if (!isAuthenticated()) {
      sessionStorage.setItem(STASH_KEY, token);
      navigate({ to: "/login", replace: true });
      return;
    }

    (async () => {
      try {
        const r = await projectsApi.acceptInvite(token);
        sessionStorage.removeItem(STASH_KEY);
        navigate({ to: "/app/projects/$pid", params: { pid: r.project_id }, replace: true });
      } catch (err) {
        setStatus("error");
        setErrMsg(err instanceof ApiError ? err.message : "Invite couldn't be accepted.");
      }
    })();
  }, [token, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="card w-full max-w-md text-center">
        {status === "working" ? (
          <>
            <h1 className="text-xl font-semibold">Joining project…</h1>
            <p className="mt-2 text-sm text-ink/65">One moment.</p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-red-700">Invite invalid</h1>
            <p className="mt-2 text-sm text-ink/65">{errMsg}</p>
            <a href="/app" className="btn-primary mt-6 inline-block">Go to dashboard</a>
          </>
        )}
      </div>
    </div>
  );
}
